import {
  WAIT_TIP_FORBIDDEN_COPY,
  WAIT_TIP_SLUG_MAX,
  WAIT_TIP_TEXT_MAX,
  type AdminWaitTip,
  type AdminWaitTipAudience,
  type AdminWaitTipCreateRequest,
  type AdminWaitTipUpdateRequest,
  type WaitTipIconName,
} from '@steado/admin-contract/wait-tips';

export interface WaitTipFormValues {
  readonly slug: string;
  readonly locale: string;
  readonly iconKey: WaitTipIconName;
  readonly text: string;
  readonly weight: number;
  readonly isActive: boolean;
  readonly audience: AdminWaitTipAudience;
}

export const EMPTY_AUDIENCE: AdminWaitTipAudience = {
  dietType: [],
  goal: [],
  sex: [],
  experienceLevel: [],
};

export const EMPTY_WAIT_TIP_FORM: WaitTipFormValues = {
  slug: '',
  locale: 'en-IN',
  iconKey: 'utensils',
  text: '',
  weight: 10,
  isActive: true,
  audience: EMPTY_AUDIENCE,
};

export function formValuesOf(tip: AdminWaitTip): WaitTipFormValues {
  return {
    slug: tip.slug,
    locale: tip.locale,
    iconKey: tip.iconKey,
    text: tip.text,
    weight: tip.weight,
    isActive: tip.isActive,
    audience: tip.audience,
  };
}

/**
 * Stated to the operator rather than left to a disabled button with no reason.
 *
 * These repeat what the API enforces. That duplication is deliberate: the API
 * is the rule, and this is the operator finding out before they hit save
 * rather than after.
 */
export function formIssues(values: WaitTipFormValues, isNew: boolean): string[] {
  const issues: string[] = [];
  const text = values.text.trim();

  if (isNew) {
    const slug = values.slug.trim();
    if (slug.length === 0) issues.push('A slug is required — it is what the app keys the tip on.');
    else if (slug.length > WAIT_TIP_SLUG_MAX) {
      issues.push(`The slug is over ${String(WAIT_TIP_SLUG_MAX)} characters.`);
    } else if (!/^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(slug)) {
      issues.push('Use lower-case words separated by hyphens for the slug, e.g. swap-meal.');
    }
    if (values.locale.trim().length < 2) issues.push('A locale is required.');
  }

  if (text.length === 0) issues.push('The tip needs some copy.');
  if (text.length > WAIT_TIP_TEXT_MAX) {
    issues.push(
      `The copy is over ${String(WAIT_TIP_TEXT_MAX)} characters, which wraps to a fourth line and pushes the layout.`,
    );
  }
  const forbidden = WAIT_TIP_FORBIDDEN_COPY.exec(text);
  if (forbidden !== null) {
    issues.push(
      `A tip cannot say the plan is “${forbidden[0]}” — only the plan's own status says that, and copy that ` +
        'claimed it on a timer once sent users to an empty home screen.',
    );
  }
  if (!Number.isInteger(values.weight) || values.weight < 1 || values.weight > 100) {
    issues.push('Weight must be a whole number between 1 and 100.');
  }
  return issues;
}

export function toCreateRequest(values: WaitTipFormValues): AdminWaitTipCreateRequest {
  return {
    slug: values.slug.trim(),
    locale: values.locale.trim(),
    iconKey: values.iconKey,
    text: values.text.trim(),
    weight: values.weight,
    isActive: values.isActive,
    audience: values.audience,
  };
}

export function toUpdateRequest(values: WaitTipFormValues): AdminWaitTipUpdateRequest {
  return {
    iconKey: values.iconKey,
    text: values.text.trim(),
    weight: values.weight,
    isActive: values.isActive,
    audience: values.audience,
  };
}

/** "Everyone" when nothing is chosen, otherwise the chosen values, in order. */
export function audienceSummary(audience: AdminWaitTipAudience): string {
  const parts = [...audience.dietType, ...audience.goal, ...audience.sex, ...audience.experienceLevel].map(
    (value) => value.replace(/_/g, ' '),
  );
  return parts.length === 0 ? 'Everyone' : parts.join(', ');
}
